import {
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type AnchorHTMLAttributes,
    type HTMLAttributes,
} from "react";
import { MarkdownHooks, type Components, type Options } from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeMermaid from "rehype-mermaid";
import rehypeSlug from "rehype-slug";
import { syncMermaidTheme } from "../lib/mermaid";

type HeadingTag = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

function decodeHashId(raw: string): string {
    try {
        return decodeURIComponent(raw);
    } catch {
        return raw;
    }
}

function hashIdFromHref(href: string | undefined): string | null {
    if (!href) return null;
    if (href.startsWith("#") && href.length > 1) {
        return decodeHashId(href.slice(1));
    }
    try {
        const url = new URL(href, window.location.href);
        if (url.pathname === window.location.pathname && url.hash.length > 1) {
            return decodeHashId(url.hash.slice(1));
        }
    } catch {
        return null;
    }
    return null;
}

function MarkdownAnchor({
    href,
    children,
    onClick,
    node: _node,
    ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }) {
    return (
        <a
            href={href}
            {...props}
            onClick={(event) => {
                onClick?.(event);
                if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                    return;
                }

                const id = hashIdFromHref(href);
                if (!id) return;

                const target = document.getElementById(id);
                if (!target) return;

                event.preventDefault();
                event.stopPropagation();
                target.scrollIntoView({ behavior: "smooth", block: "start" });
                if (window.location.hash !== `#${id}`) {
                    history.pushState(null, "", `#${id}`);
                }
            }}
        >
            {children}
        </a>
    );
}

function MarkdownHeading({
    Tag,
    id,
    children,
    node: _node,
    ...props
}: HTMLAttributes<HTMLHeadingElement> & { Tag: HeadingTag; node?: unknown }) {
    const ref = useRef<HTMLHeadingElement>(null);

    // MarkdownHooks renders asynchronously (Mermaid). If the URL already has a
    // hash, scroll once the matching heading actually exists in the DOM.
    useLayoutEffect(() => {
        if (!id) return;
        if (decodeHashId(window.location.hash.slice(1)) === id) {
            ref.current?.scrollIntoView({ block: "start" });
        }
    }, [id]);

    return (
        <Tag ref={ref} id={id} {...props}>
            {children}
        </Tag>
    );
}

const markdownComponents: Components = {
    a: MarkdownAnchor,
    h1: (props) => <MarkdownHeading Tag="h1" {...props} />,
    h2: (props) => <MarkdownHeading Tag="h2" {...props} />,
    h3: (props) => <MarkdownHeading Tag="h3" {...props} />,
    h4: (props) => <MarkdownHeading Tag="h4" {...props} />,
    h5: (props) => <MarkdownHeading Tag="h5" {...props} />,
    h6: (props) => <MarkdownHeading Tag="h6" {...props} />,
};

interface MarkdownContentProps {
    children: string;
    /** Allow raw HTML in markdown (needed for editor preview edit diffs). */
    allowRaw?: boolean;
}

function useDocumentTheme() {
    const [theme, setTheme] = useState(() =>
        typeof document !== "undefined"
            ? document.documentElement.getAttribute("data-theme") || "night"
            : "night",
    );

    useEffect(() => {
        const root = document.documentElement;
        const observer = new MutationObserver(() => {
            setTheme(root.getAttribute("data-theme") || "night");
        });
        observer.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
        return () => observer.disconnect();
    }, []);

    return theme;
}

export function MarkdownContent({ children, allowRaw = false }: MarkdownContentProps) {
    const theme = useDocumentTheme();
    const reactId = useId().replace(/:/g, "");
    const syncedTheme = useRef<string | null>(null);

    // Sync before MarkdownHooks starts async Mermaid work (useEffect is too late).
    if (typeof document !== "undefined" && syncedTheme.current !== theme) {
        syncMermaidTheme();
        syncedTheme.current = theme;
    }

    const rehypePlugins = useMemo<Options["rehypePlugins"]>(() => {
        const mermaid = [
            rehypeMermaid,
            {
                // Unique prefix avoids ID collisions when multiple MarkdownHooks remount.
                prefix: `mermaid-${reactId}`,
                errorFallback: (_el, diagram, error) => {
                    console.warn("Mermaid diagram failed to render:", error);
                    return {
                        type: "element",
                        tagName: "pre",
                        properties: { className: ["mermaid-error"] },
                        children: [
                            {
                                type: "element",
                                tagName: "code",
                                properties: { className: ["language-mermaid"] },
                                children: [{ type: "text", value: diagram }],
                            },
                        ],
                    };
                },
            },
        ] as const;

        return allowRaw ? [rehypeRaw, rehypeSlug, mermaid] : [rehypeSlug, mermaid];
    }, [allowRaw, reactId]);

    return (
        <MarkdownHooks
            // Remount on theme change so diagrams re-render with the new palette.
            key={theme}
            components={markdownComponents}
            rehypePlugins={rehypePlugins}
            fallback={
                <div className="flex items-center gap-2 text-sm opacity-60 py-2">
                    <span className="loading loading-spinner loading-sm" />
                    <span>Rendering…</span>
                </div>
            }
        >
            {children}
        </MarkdownHooks>
    );
}
